// src/admin/presets/PresetReviewCard.tsx
import React, { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';

interface PresetReviewCardProps {
  candidate: any;
  onAccept?: (id: string) => void;
  onReject?: (id: string) => void;
  onUpdate?: (id: string, updates: any) => void;
  onClose: () => void;
}

const PresetReviewCard: React.FC<PresetReviewCardProps> = ({
  candidate,
  onAccept,
  onReject,
  onUpdate,
  onClose,
}) => {
  const [editing, setEditing] = useState(false);
  const [editedPreset, setEditedPreset] = useState(candidate.preset);

  const handleSave = () => {
    if (onUpdate) {
      onUpdate(candidate.id, editedPreset);
      setEditing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="bg-white max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-2xl font-bold">{editedPreset.name}</h2>
            <Button onClick={onClose} variant="ghost">✕</Button>
          </div>

          {/* Ostrzeżenie */}
          <div className="bg-yellow-50 border border-yellow-200 p-3 rounded mb-4">
            ⚠️ Obrazy i treści tylko do researchu - nie używaj komercyjnie
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Lewa kolumna */}
            <div className="space-y-4">
              <div>
                <label className="font-semibold">Kategoria:</label>
                {editing ? (
                  <select
                    value={editedPreset.category}
                    onChange={(e) => setEditedPreset({...editedPreset, category: e.target.value})}
                    className="w-full border rounded p-2"
                  >
                    <option value="formatka">Formatka</option>
                    <option value="pojemnik">Pojemnik</option>
                    <option value="ekspozytor">Ekspozytor</option>
                    <option value="kaseton">Kaseton</option>
                    <option value="ledon">LED-ON</option>
                    <option value="gablota">Gablota</option>
                    <option value="obudowa">Obudowa</option>
                    <option value="impuls">Impuls</option>
                  </select>
                ) : (
                  <p>{editedPreset.category}</p>
                )}
              </div>

              <div>
                <label className="font-semibold">Materiał:</label>
                {editing ? (
                  <div className="flex gap-2">
                    <select
                      value={editedPreset.material?.type}
                      onChange={(e) => setEditedPreset({
                        ...editedPreset,
                        material: {...editedPreset.material, type: e.target.value}
                      })}
                      className="flex-1 border rounded p-2"
                    >
                      <option value="PMMA">PMMA</option>
                      <option value="PET-G">PET-G</option>
                      <option value="HIPS">HIPS</option>
                      <option value="PC">PC</option>
                      <option value="DIBOND">DIBOND</option>
                      <option value="PCV">PCV</option>
                    </select>
                    <input
                      type="number"
                      value={editedPreset.material?.thickness}
                      onChange={(e) => setEditedPreset({
                        ...editedPreset,
                        material: {...editedPreset.material, thickness: Number(e.target.value)}
                      })}
                      className="w-20 border rounded p-2"
                      placeholder="mm"
                    />
                  </div>
                ) : (
                  <p>{editedPreset.material?.type} {editedPreset.material?.thickness}mm</p>
                )}
              </div>

              <div>
                <label className="font-semibold">Wymiary (mm):</label>
                {editing ? (
                  <div className="flex gap-2">
                    <input
                      type="number"
                      value={editedPreset.geometry?.width}
                      onChange={(e) => setEditedPreset({
                        ...editedPreset,
                        geometry: {...editedPreset.geometry, width: Number(e.target.value)}
                      })}
                      className="flex-1 border rounded p-2"
                      placeholder="Szerokość"
                    />
                    <input
                      type="number"
                      value={editedPreset.geometry?.height}
                      onChange={(e) => setEditedPreset({
                        ...editedPreset,
                        geometry: {...editedPreset.geometry, height: Number(e.target.value)}
                      })}
                      className="flex-1 border rounded p-2"
                      placeholder="Wysokość"
                    />
                    <input
                      type="number"
                      value={editedPreset.geometry?.depth}
                      onChange={(e) => setEditedPreset({
                        ...editedPreset,
                        geometry: {...editedPreset.geometry, depth: Number(e.target.value)}
                      })}
                      className="flex-1 border rounded p-2"
                      placeholder="Głębokość"
                    />
                  </div>
                ) : (
                  <p>
                    {editedPreset.geometry?.width} x {editedPreset.geometry?.height} x {editedPreset.geometry?.depth}
                  </p>
                )}
              </div>

              <div>
                <label className="font-semibold">Opis:</label>
                {editing ? (
                  <textarea
                    value={editedPreset.description}
                    onChange={(e) => setEditedPreset({...editedPreset, description: e.target.value})}
                    className="w-full border rounded p-2"
                    rows={3}
                  />
                ) : (
                  <p>{editedPreset.description || 'Brak opisu'}</p>
                )}
              </div>

              <div>
                <label className="font-semibold">Źródło:</label>
                <a 
                  href={candidate.sourceUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {new URL(candidate.sourceUrl).hostname}
                </a>
              </div>
            </div>

            {/* Prawa kolumna - kalkulacja */}
            <div className="space-y-4">
              <h3 className="font-bold text-lg">Kalkulacja ceny netto:</h3>
              
              {candidate.pricing ? (
                <div className="bg-gray-50 p-4 rounded space-y-2">
                  <div className="flex justify-between">
                    <span>Materiał:</span>
                    <span>{candidate.pricing.breakdown?.materialPerM2?.toFixed(2)} zł/m²</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Powierzchnia:</span>
                    <span>{candidate.pricing.breakdown?.area?.toFixed(3)} m²</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Odpad ({candidate.pricing.breakdown?.wastePercent}%):</span>
                    <span>{((candidate.pricing.breakdown?.area || 0) * (candidate.pricing.breakdown?.wastePercent || 0) / 100).toFixed(3)} m²</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Mnożnik zysku:</span>
                    <span>x{candidate.pricing.breakdown?.marginMultiplier}</span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between font-bold text-lg">
                    <span>SUMA NETTO:</span>
                    <span className="text-green-600">{candidate.pricing.totalNet?.toFixed(2)} PLN</span>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500">Brak kalkulacji ceny</p>
              )}

              {/* Opcje dodatkowe */}
              <div>
                <h4 className="font-semibold mb-2">Opcje dodatkowe:</h4>
                <div className="space-y-1">
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" />
                    Grafika (75 zł/m²)
                  </label>
                  <label className="flex items-center">
                    <input type="checkbox" className="mr-2" />
                    Polerowanie krawędzi (5 zł/mb)
                  </label>
                  {editedPreset.category === 'gablota' && (
                    <>
                      <label className="flex items-center">
                        <input type="checkbox" className="mr-2" />
                        Zawias (6 zł/szt)
                      </label>
                      <label className="flex items-center">
                        <input type="checkbox" className="mr-2" />
                        Zamek (12 zł/szt)
                      </label>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Przyciski akcji */}
          <div className="flex justify-between mt-6 pt-4 border-t">
            <div className="flex gap-2">
              {!editing && onUpdate && (
                <Button
                  onClick={() => setEditing(true)}
                  variant="outline"
                >
                  ✏️ Edytuj
                </Button>
              )}
              {editing && (
                <>
                  <Button
                    onClick={handleSave}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    💾 Zapisz zmiany
                  </Button>
                  <Button
                    onClick={() => {
                      setEditedPreset(candidate.preset);
                      setEditing(false);
                    }}
                    variant="outline"
                  >
                    Anuluj
                  </Button>
                </>
              )}
            </div>
            
            {!editing && (
              <div className="flex gap-2">
                {onReject && (
                  <Button
                    onClick={() => onReject(candidate.id)}
                    variant="outline"
                    className="border-red-600 text-red-600 hover:bg-red-50"
                  >
                    ✗ Odrzuć
                  </Button>
                )}
                {onAccept && (
                  <Button
                    onClick={() => onAccept(candidate.id)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    ✓ Akceptuj i dodaj do presetów
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PresetReviewCard;
